import { useRef, useCallback } from 'react';
import { useChatStore } from '../store/chatStore';
import { api } from '../services/api';
import type { GuardrailsMeta, EraxMeta } from '../types';
import type { SchemaResult } from '../services/schemaQueryApi';

/** Extract guardrails metadata from the backend ChatResponse metadata map. */
function extractGuardrailsMeta(metadata: Record<string, unknown>): GuardrailsMeta | undefined {
  if (!metadata) return undefined;
  const active = metadata.guardrails_active === true || metadata.guardrails_blocked === true;
  if (!active) return undefined;
  return {
    active: true,
    blocked: metadata.guardrails_blocked === true,
    blockedReasons: Array.isArray(metadata.guardrails_reason)
      ? (metadata.guardrails_reason as string[])
      : undefined,
    inputPiiMasked: metadata.guardrails_input_pii_masked === true,
    outputPiiMasked: metadata.guardrails_output_pii_masked === true,
  };
}

/** Extract the resolved schema (module or table) from the ChatResponse metadata. */
function extractTicketKeys(metadata: Record<string, unknown>): string[] | undefined {
  if (!metadata) return undefined;
  const keys = metadata.ticket_keys;
  if (!Array.isArray(keys) || keys.length === 0) return undefined;
  return keys.filter((k): k is string => typeof k === 'string');
}

function extractSchemaData(metadata: Record<string, unknown>): SchemaResult | undefined {
  if (!metadata || metadata.intent !== 'schema_query') return undefined;
  const data = metadata.schema_data;
  if (!data || typeof data !== 'object') return undefined;
  const result = data as SchemaResult;
  if (!Array.isArray(result.tables)) return undefined;
  return result;
}

/** Extract ERAX metadata from the backend ChatResponse metadata map. */
function extractEraxMeta(metadata: Record<string, unknown>): EraxMeta | undefined {
  if (!metadata || metadata.intent !== 'data_query') return undefined;
  return {
    sessionId: metadata.erax_session_id as string | undefined,
    sqlQuery: metadata.sql_query as string | undefined,
    module: metadata.erax_module as string | undefined,
    datacube: metadata.erax_datacube as string | undefined,
    chart: metadata.erax_chart,
    data: metadata.erax_data,
    suggestedQuestions: Array.isArray(metadata.erax_suggested_questions)
      ? (metadata.erax_suggested_questions as string[])
      : undefined,
  };
}

/**
 * Hook for sending non-streaming chat messages to the backend.
 *
 * Manages the full request lifecycle: adds user and placeholder assistant
 * messages to the store, calls the `/chat` API, and updates the assistant
 * message with the response (or an error). Supports request cancellation
 * via {@link AbortController}.
 *
 * JIRA ticket lookup, Event Timeline, and forced-ERAX modes have been
 * removed from the chat interface — those features are now served
 * exclusively through the Issue Analysis module. The intent router in the
 * backend still auto-routes data queries to ERAX when appropriate.
 *
 * @returns `sendMessage` to dispatch a query and `abortRequest` to cancel
 *          an in-flight request.
 */
export function useChat() {
  const abortControllerRef = useRef<AbortController | null>(null);

  const {
    sessionId,
    persona,
    rfpSubsection,
    addMessage,
    updateMessage,
    setLoading,
  } = useChatStore();

  const sendMessage = useCallback(async (query: string, attachments?: import('../types').ChatAttachment[]) => {
    // Set loading immediately to block duplicate submissions
    setLoading(true, 'searching');

    // Add user message (image-only sends show a marker instead of empty text)
    const attachmentNote = attachments && attachments.length > 0
      ? `\n\n📎 ${attachments.map(a => a.filename).join(', ')}`
      : '';
    addMessage({
      role: 'user',
      content: (query || '(image attached)') + attachmentNote,
    });

    // Create placeholder for assistant message
    const assistantMessageId = addMessage({
      role: 'assistant',
      content: '',
      isLoading: true,
    });

    // Create abort controller for this request
    abortControllerRef.current = new AbortController();

    try {
      // Update status to generating
      setTimeout(() => {
        setLoading(true, 'generating');
      }, 500);

      // Make API request
      const response = await api.chat(
        {
          query,
          persona,
          session_id: sessionId,
          include_sources: true,
          ...(attachments && attachments.length > 0 ? { attachments } : {}),
          ...(persona === 'rfp' && rfpSubsection ? { rfp_subsection: rfpSubsection } : {}),
        },
        abortControllerRef.current.signal
      );

      // Update assistant message with response
      updateMessage(assistantMessageId, {
        content: response.answer,
        confidence: response.confidence,
        confidenceLevel: response.confidence_level,
        sources: response.sources,
        queryType: response.query_type,
        clarifyingQuestions: response.clarifying_questions,
        needsClarification: response.needs_clarification,
        guardrails: extractGuardrailsMeta(response.metadata),
        eraxMeta: extractEraxMeta(response.metadata),
        schemaData: extractSchemaData(response.metadata),
        ticketKeys: extractTicketKeys(response.metadata),
        pipeline: Array.isArray(response.metadata?.pipeline)
          ? (response.metadata.pipeline as import('../types').PipelineStage[])
          : undefined,
        isLoading: false,
      });
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        // Request was cancelled
        updateMessage(assistantMessageId, {
          content: '*Response generation was stopped.*',
          isLoading: false,
        });
      } else {
        // Handle error
        console.error('Chat error:', error);
        const errorMsg = error instanceof Error ? error.message : 'Unknown error';
        updateMessage(assistantMessageId, {
          content: 'Sorry, I encountered an error while processing your request. Please try again.',
          isLoading: false,
          confidenceLevel: 'low',
        });
      }
    } finally {
      setLoading(false);
      abortControllerRef.current = null;
    }
  }, [sessionId, persona, rfpSubsection, addMessage, updateMessage, setLoading]);

  const abortRequest = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  return {
    sendMessage,
    abortRequest,
  };
}
